import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  formatInstant,
  links,
  resolveFormattingLocale,
  rowsOf,
  type DatasetField,
} from '@eis/core/reporting';
import {
  ConfirmAction,
  EmptyState,
  ErrorState,
  PageHeader,
  PageLayout,
  RequirePermission,
  cardSurfaceSx,
} from '@eis/patterns';
import {
  CatalogTree,
  displayFieldName,
  typeBadge,
  type CatalogSelection,
} from '@eis/patterns/reporting';
import { reportingPermissions as p } from '../../../permissions';
import { codeMessage } from '../../../lib/codes';
import { useReportWithDataset } from '../../reports-list/ui/ReportsListPage';
import {
  reportingReasonOf,
  useCreateCustomDataset,
  useCustomDataset,
  useDataset,
  useDatasets,
  useDeleteCustomDataset,
  useQueryPolling,
  useRefreshCatalog,
  useSampleDataset,
} from '../api/datasets';
import { newDatasetDocument } from '../model/datasets';
import { DatasetPicker } from '../../reports-list/ui/DatasetPicker';
import { DatasetPreview, FieldPicker } from './DatasetPreview';

export interface DatasetsSearch {
  readonly dataset?: string;
  readonly custom?: string;
  readonly q?: string;
}

export interface DatasetsPageProps {
  readonly search: DatasetsSearch;
  readonly onSearchChange: (search: DatasetsSearch) => void;
}

/**
 * Datensätze (SDD-024 W-3…W-6, L-2, S-5, S-6; mockup board 7): the tenant's catalog grouped as the
 * W-4 rules say, the custom datasets under "Meine Datensätze", and the sheet of the one selected —
 * its fields grouped with their types, example values on demand (a LIMIT 5 query, never
 * pre-fetched), "Daten vom", and "Bericht mit diesem Datensatz". A custom dataset shows which
 * version reports use; one in use cannot be archived.
 */
export function DatasetsPage({ search, onSearchChange }: DatasetsPageProps): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const datasets = useDatasets();
  const refresh = useRefreshCatalog();
  const [creating, setCreating] = useState(false);
  const reportWith = useReportWithDataset();
  const selection: CatalogSelection | undefined =
    search.custom !== undefined
      ? { kind: 'custom', code: search.custom }
      : search.dataset !== undefined
        ? { kind: 'catalog', name: search.dataset }
        : undefined;

  const offered = (datasets.data?.datasets ?? []).length;
  const fetchedAt = datasets.data?.fetchedAt;
  const header = (
    <PageHeader
      title={t('datasets.title')}
      description={
        fetchedAt === undefined
          ? t('datasets.catalogNever')
          : t('datasets.catalogFrom', { at: formatInstant(fetchedAt, locale), count: offered })
      }
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {datasets.data?.refreshing === true && (
            <Chip size="small" color="info" label={t('datasets.refreshing')} />
          )}
          <RequirePermission anyOf={[p.datasets.view]}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshOutlinedIcon />}
              disabled={refresh.isPending || datasets.data?.refreshing === true}
              onClick={() => {
                refresh.mutate({});
              }}
            >
              {t('datasets.refresh')}
            </Button>
          </RequirePermission>
          <RequirePermission anyOf={[p.datasets.manage]}>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('datasets.newDataset')}
            </Button>
          </RequirePermission>
        </Stack>
      }
    />
  );

  if (datasets.isError) {
    const reason = reportingReasonOf(datasets.error);
    return (
      <PageLayout header={header}>
        <ErrorState
          description={reason === undefined ? t('datasets.loadError') : codeMessage(t, reason).hint}
          onRetry={() => void datasets.refetch()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout header={header} maxWidth="none">
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '320px minmax(0, 1fr)' },
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined" sx={(theme) => ({ p: 1.5, ...cardSurfaceSx(theme) })}>
          <CatalogTree
            datasets={(datasets.data?.datasets ?? []).map((d) => ({
              name: d.name ?? '',
              group: d.group ?? 'STANDARD',
              ...(d.product === undefined ? {} : { product: d.product }),
              ...(d.datasetKind === undefined ? {} : { datasetKind: d.datasetKind }),
              ...(d.productVersion === undefined ? {} : { productVersion: d.productVersion }),
              ...(d.fieldCount === undefined ? {} : { fieldCount: d.fieldCount }),
            }))}
            custom={(datasets.data?.customDatasets ?? []).map((c) => ({
              code: c.code ?? '',
              name: c.name ?? '',
              publishedVersion: c.publishedVersion ?? 0,
              hasDraft: c.hasDraft === true,
            }))}
            {...(selection === undefined ? {} : { selected: selection })}
            onSelect={(next) => {
              onSearchChange(
                next.kind === 'catalog'
                  ? { dataset: next.name, ...(search.q === undefined ? {} : { q: search.q }) }
                  : { custom: next.code, ...(search.q === undefined ? {} : { q: search.q }) },
              );
            }}
            search={search.q ?? ''}
            onSearch={(q) => {
              onSearchChange({ ...search, ...(q === '' ? { q: undefined } : { q }) });
            }}
          />
        </Paper>
        <Paper variant="outlined" sx={(theme) => ({ p: 2, minWidth: 0, ...cardSurfaceSx(theme) })}>
          {selection === undefined && <EmptyState compact title={t('datasets.selectHint')} />}
          {selection?.kind === 'catalog' && (
            <CatalogSheet
              name={selection.name}
              onReport={() => {
                reportWith.start({ kind: 'CATALOG', name: selection.name }, selection.name);
              }}
            />
          )}
          {selection?.kind === 'custom' && (
            <CustomSheet
              code={selection.code}
              onReport={(version, name) => {
                reportWith.start({ kind: 'CUSTOM', code: selection.code, version }, name);
              }}
              onArchived={() => {
                onSearchChange({});
              }}
            />
          )}
        </Paper>
      </Box>
      <NewDatasetDialog
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onCreated={(code) => {
          void navigate({ href: links.reporting.editDataset(code) });
        }}
      />
    </PageLayout>
  );
}

function FieldsTable({
  fields,
  examples,
}: {
  readonly fields: readonly DatasetField[];
  readonly examples?: ReadonlyMap<string, string>;
}): ReactNode {
  const { t } = useTranslation('reporting');
  return (
    <TableContainer sx={{ maxHeight: 520 }} tabIndex={0}>
      <Table size="small" stickyHeader aria-label={t('datasets.fields')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('datasets.columns.field')}</TableCell>
            <TableCell>{t('datasets.columns.type')}</TableCell>
            <TableCell>{t('datasets.columns.group')}</TableCell>
            {examples !== undefined && <TableCell>{t('datasets.columns.example')}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((f) => (
            <TableRow key={f.name} data-field={f.name}>
              <TableCell>{displayFieldName(f.name ?? '')}</TableCell>
              <TableCell>{typeBadge(f.type ?? 'STRING')}</TableCell>
              <TableCell>{f.group}</TableCell>
              {examples !== undefined && <TableCell>{examples.get(f.name ?? '') ?? ''}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CatalogSheet({
  name,
  onReport,
}: {
  readonly name: string;
  readonly onReport: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const dataset = useDataset(name);
  const sample = useSampleDataset();
  const [sampleId, setSampleId] = useState<string | undefined>(undefined);
  const polling = useQueryPolling(sampleId);
  const examples = useMemo(() => {
    const query = polling.query;
    if (query?.state !== 'SUCCEEDED') return undefined;
    const cols = (query.columns ?? []).map((c) => c.key ?? '');
    const map = new Map<string, string>();
    for (const row of rowsOf(query.rows)) {
      row.forEach((cell, i) => {
        const key = cols[i] ?? '';
        if (cell !== null && cell !== '' && !map.has(key)) map.set(key, cell);
      });
    }
    return map;
  }, [polling.query]);
  const fields = dataset.data?.fields ?? [];
  const groups = [...new Set(fields.map((f) => f.group ?? ''))];
  return (
    <Stack spacing={1.5} data-testid="dataset-sheet">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="h3" component="h2">
          {name}
        </Typography>
        {dataset.data?.dataAsOf !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {t('datasets.dataAsOf', { at: formatInstant(dataset.data.dataAsOf, locale) })}
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
        aria-label={t('datasets.fields')}
      >
        {groups.map((g) => (
          <Chip key={g} size="small" variant="outlined" label={g} />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <RequirePermission anyOf={[p.queries.run]}>
          <Button
            size="small"
            variant="outlined"
            disabled={sample.isPending}
            onClick={() => {
              sample.mutate(
                { name },
                {
                  onSuccess: (q) => {
                    setSampleId(q.id);
                  },
                },
              );
            }}
          >
            {t('datasets.loadExamples')}
          </Button>
        </RequirePermission>
        <RequirePermission anyOf={[p.reports.create]}>
          <Button size="small" variant="contained" onClick={onReport}>
            {t('datasets.reportWith')}
          </Button>
        </RequirePermission>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('datasets.examplesHint')}
      </Typography>
      <FieldsTable fields={fields} {...(examples === undefined ? {} : { examples })} />
    </Stack>
  );
}

function CustomSheet({
  code,
  onReport,
  onArchived,
}: {
  readonly code: string;
  readonly onReport: (version: number, name: string) => void;
  readonly onArchived: () => void;
}): ReactNode {
  const { t } = useTranslation('reporting');
  const navigate = useNavigate();
  const dataset = useCustomDataset(code);
  const remove = useDeleteCustomDataset();
  const [archiving, setArchiving] = useState(false);
  const head = dataset.data?.dataset;
  if (head === undefined) return null;
  const usage = head.usage ?? [];
  const inUse = usage.some((u) => (u.reports ?? 0) > 0);
  const reason = remove.error === null ? undefined : reportingReasonOf(remove.error);
  return (
    <Stack spacing={1.5} data-testid="dataset-sheet">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="h3" component="h2">
          {head.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {head.code}
        </Typography>
        {(head.publishedVersion ?? 0) > 0 && (
          <Chip size="small" label={t('version.published', { version: head.publishedVersion })} />
        )}
        {head.hasDraft === true && (
          <Chip
            size="small"
            variant="outlined"
            label={t('version.draft', { version: (head.publishedVersion ?? 0) + 1 })}
          />
        )}
      </Stack>
      <Typography variant="body2">
        {t('datasets.sources', { sources: (head.sources ?? []).join(' × ') })}
      </Typography>
      {usage.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('datasets.noUsage')}
        </Typography>
      ) : (
        usage.map((u) => (
          <Typography key={u.version} variant="body2">
            {t('datasets.usage', { count: u.reports ?? 0, version: u.version ?? 0 })}
          </Typography>
        ))
      )}
      <Alert severity="info">{t('datasets.joinCaveat')}</Alert>
      {reason !== undefined && <Alert severity="error">{codeMessage(t, reason).title}</Alert>}
      <Stack direction="row" spacing={1}>
        {head.editable === true && (
          <RequirePermission anyOf={[p.datasets.manage]}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void navigate({ href: links.reporting.editDataset(code) })}
            >
              {t('datasets.edit')}
            </Button>
          </RequirePermission>
        )}
        <RequirePermission anyOf={[p.datasets.manage]}>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={inUse || head.editable !== true}
            onClick={() => {
              setArchiving(true);
            }}
          >
            {t('datasets.archive')}
          </Button>
        </RequirePermission>
        {(head.publishedVersion ?? 0) > 0 && (
          <RequirePermission anyOf={[p.reports.create]}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                onReport(head.publishedVersion ?? 1, head.name ?? code);
              }}
            >
              {t('datasets.reportWith')}
            </Button>
          </RequirePermission>
        )}
      </Stack>
      <FieldsTable fields={dataset.data?.fields ?? []} />
      <ConfirmAction
        open={archiving}
        title={t('datasets.archiveTitle', { name: head.name ?? code })}
        description={t('datasets.archiveBody')}
        severity="destructive"
        confirmLabel={t('datasets.archive')}
        busy={remove.isPending}
        onCancel={() => {
          setArchiving(false);
        }}
        onConfirm={() => {
          remove.mutate(
            { code, etag: head.etag, version: head.version },
            {
              onSuccess: () => {
                setArchiving(false);
                onArchived();
              },
            },
          );
        }}
      />
    </Stack>
  );
}

function NewDatasetDialog({
  open,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: (code: string) => void;
}): ReactNode {
  const { t } = useTranslation('reporting');
  const create = useCreateCustomDataset();
  const [name, setName] = useState('');
  const [base, setBase] = useState('');
  const [joined, setJoined] = useState('');
  const [on, setOn] = useState('');
  const baseFields = useDataset(base === '' ? undefined : base);
  const joinedFields = useDataset(joined === '' ? undefined : joined);
  const common = useMemo(() => {
    const b = new Set((joinedFields.data?.fields ?? []).map((f) => f.name ?? ''));
    return (baseFields.data?.fields ?? []).map((f) => f.name ?? '').filter((n) => b.has(n));
  }, [baseFields.data, joinedFields.data]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="new-dataset-title"
    >
      <DialogTitle id="new-dataset-title">{t('datasets.newDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size="small"
            label={t('datasets.newDialog.name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            required
          />
          <DatasetPicker
            catalogOnly
            label={t('datasets.newDialog.base')}
            value={base === '' ? '' : `CATALOG:${base}`}
            onChange={(option) => {
              setBase(option?.ref.kind === 'CATALOG' ? option.ref.name : '');
              setOn('');
            }}
          />
          {base !== '' && <DatasetPreview name={base} />}
          <DatasetPicker
            catalogOnly
            label={t('datasets.newDialog.joined')}
            value={joined === '' ? '' : `CATALOG:${joined}`}
            onChange={(option) => {
              setJoined(option?.ref.kind === 'CATALOG' ? option.ref.name : '');
              setOn('');
            }}
          />
          {joined !== '' && <DatasetPreview name={joined} />}
          <FieldPicker
            label={t('datasets.newDialog.on')}
            fields={common}
            value={on}
            disabled={common.length === 0}
            {...(base !== '' && joined !== '' && common.length === 0
              ? { helperText: t('datasets.newDialog.noCommon') }
              : {})}
            onChange={setOn}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>
          {t('datasets.newDialog.cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={
            create.isPending || name.trim() === '' || base === '' || joined === '' || on === ''
          }
          onClick={() => {
            const document = newDatasetDocument(
              name.trim(),
              base,
              joined,
              on,
              (baseFields.data?.fields ?? []).map((f) => f.name ?? ''),
            );
            create.mutate(
              { document: document as unknown as Record<string, unknown> },
              {
                onSuccess: (d) => {
                  onCreated(d.code ?? '');
                },
              },
            );
          }}
        >
          {t('datasets.newDialog.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
